import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import {
  applyReadingAnnotationsToElement,
  getReadingSelectionOffsets,
  mergeReadingAnnotation,
  restoreReadingAnnotations,
} from '../utils/readingAnnotations';

const EMPTY_ANNOTATIONS = [];

export default function QuestionAnnotations({ html, annotations = EMPTY_ANNOTATIONS, onChange, onSelection, answers, disabled = false }) {
  const rootRef = useRef(null);
  const [selection, setSelection] = useState(null);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    // Parent effects hydrate blanks/choices first; decorate text without replacing those controls.
    const timer = window.setTimeout(() => {
      const root = rootRef.current;
      if (root) applyReadingAnnotationsToElement(root, restoreReadingAnnotations(root, annotations));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [html, annotations, answers]);

  useEffect(() => {
    setSelection(null);
    setActive(null);
  }, [html, disabled]);

  useEffect(() => {
    const dismiss = () => setSelection(null);
    const dismissOutsideSelection = () => {
      const selected = window.getSelection();
      if (!selected?.rangeCount || selected.isCollapsed
        || !rootRef.current?.contains(selected.getRangeAt(0).commonAncestorContainer)) dismiss();
    };
    document.addEventListener('selectionchange', dismissOutsideSelection);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('selectionchange', dismissOutsideSelection);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, []);

  const captureSelection = () => {
    if (disabled) return;
    const selected = window.getSelection();
    const range = selected?.rangeCount && !selected.isCollapsed ? selected.getRangeAt(0) : null;
    const offsets = getReadingSelectionOffsets(rootRef.current, range);
    if (offsets?.text.trim()) onSelection?.();
    setSelection(offsets?.text.trim() ? { ...offsets, rect: range.getBoundingClientRect() } : null);
  };

  const applySelection = (type) => {
    const root = rootRef.current;
    if (!root || !selection || disabled) return;
    const next = mergeReadingAnnotation(root, restoreReadingAnnotations(root, annotations), {
      id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      start: selection.start, end: selection.end, type, note: '',
    });
    const saved = next.find((item) => item.start <= selection.start && item.end >= selection.end);
    onChange(next);
    setSelection(null);
    window.getSelection()?.removeAllRanges();
    if (type === 'note' && saved) {
      setActive(saved);
      setNote(saved.note || '');
    }
  };

  const openAnnotation = (event) => {
    if (disabled) return;
    const mark = event.target.closest?.('[data-reading-highlight-id]');
    if (!mark) {
      if (selection && event.target.closest?.('label') && !event.target.closest?.('input')) event.preventDefault();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const saved = restoreReadingAnnotations(rootRef.current, annotations)
      .find((item) => item.id === mark.dataset.readingHighlightId);
    if (!saved) return;
    setSelection(null);
    setActive(saved);
    setNote(saved.note || '');
  };

  const updateActive = (remove) => {
    if (!active || disabled) return;
    const current = restoreReadingAnnotations(rootRef.current, annotations);
    onChange(remove ? current.filter((item) => item.id !== active.id) : current.map((item) => (
      item.id === active.id ? { ...item, type: 'note', note: note.trim().slice(0, 1000) } : item
    )));
    setActive(null);
  };

  return <>
    <div ref={rootRef} className="ielts-passage-questions" data-question-annotations="true"
      onMouseUp={captureSelection} onTouchEnd={captureSelection} onKeyUp={captureSelection}
      onClickCapture={openAnnotation}
      onKeyDown={(event) => {
        if ((event.key === 'Enter' || event.key === ' ') && event.target.closest?.('[data-reading-highlight-id]')) {
          openAnnotation(event);
        }
      }}
      dangerouslySetInnerHTML={{ __html: html }} />
    {selection && !disabled && <Box sx={{
      position: 'fixed', zIndex: 1300,
      left: Math.max(8, Math.min(selection.rect.left, window.innerWidth - 112)),
      top: Math.max(8, Math.min(selection.rect.bottom + 8, window.innerHeight - 64)),
      display: 'flex', gap: 1, p: 1, bgcolor: '#fffdf5', border: '1px solid #d8d2c0',
      borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.16)',
    }}>
      <Tooltip title="Highlight selected text">
        <IconButton aria-label="Highlight selected text" onMouseDown={(event) => event.preventDefault()}
          onClick={() => applySelection('marker')} sx={{ bgcolor: '#996CFE', color: '#fff', '&:hover': { bgcolor: '#7C4DFF' } }}>
          <BorderColorIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Add note to selected text">
        <IconButton aria-label="Add note to selected text" onMouseDown={(event) => event.preventDefault()}
          onClick={() => applySelection('note')} sx={{ bgcolor: '#996CFE', color: '#fff', '&:hover': { bgcolor: '#7C4DFF' } }}>
          <NoteAddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>}
    <Dialog open={Boolean(active) && !disabled} onClose={() => setActive(null)} maxWidth="xs" fullWidth>
      <DialogTitle>{active?.type === 'note' ? 'Question note' : 'Highlighted text'}</DialogTitle>
      <DialogContent>
        {active?.type === 'note' ? <TextField autoFocus fullWidth multiline minRows={4}
          value={note} onChange={(event) => setNote(event.target.value.slice(0, 1000))}
          placeholder="Write a note for this highlight" inputProps={{ maxLength: 1000 }} sx={{ mt: 1 }} />
          : <Typography color="text.secondary">Remove this highlight?</Typography>}
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={() => updateActive(true)}>Remove</Button>
        <Button onClick={() => setActive(null)}>Cancel</Button>
        {active?.type === 'note' && <Button variant="contained" onClick={() => updateActive(false)}>Save</Button>}
      </DialogActions>
    </Dialog>
  </>;
}
